import { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function isIosSafari() {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isStandalone() {
  return window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

function PushSubscribe() {
  const [subscription, setSubscription] = useState(null);
  const [supported, setSupported] = useState(false);
  const [loading, setLoading] = useState(false);
  const [iosPrompt, setIosPrompt] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!VAPID_PUBLIC_KEY) return;

    if (isIosSafari() && !isStandalone()) {
      setIosPrompt(true);
      return;
    }

    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return;
    }
    setSupported(true);

    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        setSubscription(sub);
      });
    });
  }, []);

  async function subscribe() {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      await fetch("/.netlify/functions/push-subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sub.toJSON()),
      });
      setSubscription(sub);
    } catch (err) {
      console.error("Push subscription failed:", err);
    }
    setLoading(false);
  }

  async function unsubscribe() {
    setLoading(true);
    try {
      await fetch("/.netlify/functions/push-subscribe", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(subscription.toJSON()),
      });
      await subscription.unsubscribe();
      setSubscription(null);
    } catch (err) {
      console.error("Push unsubscribe failed:", err);
    }
    setLoading(false);
  }

  if (iosPrompt) {
    return (
      <>
        <Button variant="outline-info" size="sm" onClick={() => setShowModal(true)}>
          Notify me
        </Button>
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add to Home Screen</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Push notifications on iOS require this app to be added to your Home Screen.</p>
            <ol>
              <li>Tap the <strong>Share</strong> button <span role="img" aria-label="share icon">&#xFEFF;&#x2B06;&#xFE0F;</span> in Safari</li>
              <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
              <li>Open the app from your Home Screen</li>
              <li>Tap <strong>"Notify me"</strong> again</li>
            </ol>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Got it
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  if (!supported) return null;

  return subscription ? (
    <Button variant="outline-secondary" size="sm" onClick={unsubscribe} disabled={loading}>
      Unsubscribe
    </Button>
  ) : (
    <Button variant="outline-info" size="sm" onClick={subscribe} disabled={loading}>
      Notify me
    </Button>
  );
}

export default PushSubscribe;
